import { useState } from "react";
import { Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";

interface ManageWaitlistPopoverProps {
  eventId: string;
  onWaitlistCreated: () => void;
  hasWaitlist?: boolean;
  currentMaxWaitlistSize?: number | null;
}

export default function ManageWaitlistPopover({
  eventId,
  onWaitlistCreated,
  hasWaitlist = false,
  currentMaxWaitlistSize = null,
}: ManageWaitlistPopoverProps) {
  const { toast } = useToast();
  const [isWaitlistPopoverOpen, setIsWaitlistPopoverOpen] = useState(false);
  const [maxWaitlistSize, setMaxWaitlistSize] = useState<string>("");
  const [isCreatingWaitlist, setIsCreatingWaitlist] = useState(false);

  // Initialize with current value when popover opens for editing
  const handlePopoverOpenChange = (open: boolean) => {
    setIsWaitlistPopoverOpen(open);
    if (open && hasWaitlist && currentMaxWaitlistSize) {
      setMaxWaitlistSize(currentMaxWaitlistSize.toString());
    } else if (!open) {
      setMaxWaitlistSize("");
    }
  };

  const handleCreateOrUpdateWaitlist = async () => {
    const maxSize = parseInt(maxWaitlistSize);
    if (isNaN(maxSize) || maxSize < 1 || maxSize > 100) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid number between 1 and 100.",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingWaitlist(true);
    try {
      const isUpdate = hasWaitlist;
      const method = isUpdate ? "put" : "post";
      const url = `waitlist/event/${eventId}`;

      const response = await axiosInstance[method](url, {
        maxWaitlistSize: maxSize,
      });

      if (response.data.success) {
        toast({
          title: isUpdate ? "Waitlist Updated" : "Waitlist Created",
          description: isUpdate
            ? `Waitlist maximum size updated to ${maxSize}.`
            : `Waitlist created successfully with maximum size of ${maxSize}.`,
          variant: "success",
        });
        setIsWaitlistPopoverOpen(false);
        setMaxWaitlistSize("");
        // Notify parent component to refresh data
        onWaitlistCreated();
      } else {
        toast({
          title: "Error",
          description:
            response.data.message ||
            `Failed to ${isUpdate ? "update" : "create"} waitlist.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(
        `Error ${hasWaitlist ? "updating" : "creating"} waitlist:`,
        error,
      );
      let errorMessage = `Failed to ${
        hasWaitlist ? "update" : "create"
      } waitlist.`;
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreatingWaitlist(false);
    }
  };

  const handleCancel = () => {
    setIsWaitlistPopoverOpen(false);
    setMaxWaitlistSize("");
  };

  return (
    <Popover
      open={isWaitlistPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Users className="w-4 h-4" />
          Manage Waitlist
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold">
              {hasWaitlist ? "Update Event Waitlist" : "Create Event Waitlist"}
            </h3>
            <p className="text-sm text-muted-foreground">
              {hasWaitlist
                ? "Update the maximum number of users that can join the waitlist for this event."
                : "Set the maximum number of users that can join the waitlist for this event."}
            </p>
          </div>
          <div>
            <Label htmlFor="maxWaitlistSize">Maximum Waitlist Size</Label>
            <Input
              id="maxWaitlistSize"
              type="number"
              min="1"
              max="100"
              value={maxWaitlistSize}
              onChange={(e) => setMaxWaitlistSize(e.target.value)}
              placeholder="Enter number (1-100)"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isCreatingWaitlist}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdateWaitlist}
              disabled={isCreatingWaitlist || !maxWaitlistSize}
            >
              {isCreatingWaitlist ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {hasWaitlist ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
